<template>
  <div id="base" class="h-full justify-between">
    <div
      class="flex w-full transition-[padding] duration-300"
      :style="{ paddingLeft: `${responsiveSidebarWidth}px` }"
    >
      <Navbar
        v-if="shouldShowNavbar"
        @sidebar-width-change="handleSidebarWidthChange"
      />
      <div
        id="main-base"
        class="relative flex min-h-screen max-w-full min-w-0 flex-1 flex-col"
      >
        <div class="flex-1">
          <PageFrame>
            <RouterView v-slot="{ Component, route: shown }">
              <component :is="Component" :key="pageKey(shown)" />
            </RouterView>
          </PageFrame>
        </div>
        <Footer v-if="route.meta.includeFooter ?? true" class="mt-auto" />
      </div>
      <!-- The global sidepanel: decides its own presentation (docked flex
           child here at ≥3xl, fixed overlay + edge toggle stack otherwise). -->
      <Sidepanel v-if="shouldShowSidepanel" />
    </div>
    <div id="table-preview-teleport"></div>
  </div>
  <PopupMessageOverlay
    v-if="authStore.isAuthed || showRefreshPopup"
    :show-refresh-popup="showRefreshPopup"
    @refresh-page="onRefreshPage"
  />
  <!-- <VueQueryDevtools button-position="bottom-left" /> -->
</template>

<script setup lang="ts">
import { useLogoutInterceptor } from "@/services/api";
import {
  loginRedirect,
  useRequiresAuthNavigationGuard,
  useHomeRouteRedirectGuard,
} from "@/router";
import { computed, ref, watch } from "vue";
import Navbar from "@/components/Navigation/Navbar.vue";
import Footer from "@/components/Core/Footer.vue";
import {
  type RouteLocationNormalizedLoaded,
  useRoute,
  useRouter,
} from "vue-router";
import { useAuthStore } from "@/store/auth";
import PopupMessageOverlay from "@/components/Core/PopupMessages/PopupMessageOverlay.vue";
import { useNewVersionCheck } from "@/services/version";
import { useSetDefaultTracking, useTrackLogin } from "@/services/gtm";
import {
  watchRouteForAccessTier,
  watchRouteForFeatureFlag,
  watchRouteForOrganization,
  watchRouteForUserRole,
  watchRouteForPermissionRequirement,
  watchRouteForResetFilters,
} from "@/router";
import { useQueryClient } from "@tanstack/vue-query";
import { useHead } from "@unhead/vue";
import { useUpdateTemporalFilter } from "./store/data_filters";
import { provideSubmenuOverride } from "@/utils/submenuOverride";
// import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useBp } from "@/utils/breakpoints";
import Sidepanel from "@/components/Sidepanel/Sidepanel.vue";
import PageFrame from "@/components/Shell/PageFrame.vue";
import { resetSidepanelState } from "@/composables/useSidepanel";
import { resetAiChatSession } from "@/composables/useAiChatSession";
import { provideShellDocks } from "@/composables/useShellDocks";
import { writeSidepanelWidth } from "@/composables/useSidepanelDock";
import { sidebarWidthPx } from "@/composables/useSidebarDock";
import { useShellVisibility } from "@/composables/useShellVisibility";
import { useRouteTitle } from "@/composables/useRouteTitle";
import {
  onSelectedOrganizationChange,
  useOrganizationChangedInterceptor,
} from "@/services/user";

const router = useRouter();
const authStore = useAuthStore();
const route = useRoute();

function pageKey(shown: RouteLocationNormalizedLoaded): string | undefined {
  const param = shown.meta.remountOnParam;
  return param ? `${String(shown.name)}:${shown.params[param]}` : undefined;
}

const bp = useBp();
const sidebarWidth = ref(56); // Default to collapsed width
const responsiveSidebarWidth = computed(() =>
  bp.smaller.lg ? 0 : sidebarWidth.value,
);
const { sidepanelDock } = provideShellDocks();
const isSidepanelDocked = sidepanelDock.isDocked;

const { shouldShowNavbar, shouldShowSidepanel } = useShellVisibility();
watch(
  shouldShowNavbar,
  (showNavbar) => {
    if (!showNavbar) {
      sidebarWidth.value = 0;
    }
  },
  { immediate: true },
);

provideSubmenuOverride();
function handleSidebarWidthChange(width: number): void {
  sidebarWidth.value = width;
}

watch(
  responsiveSidebarWidth,
  (w) => {
    document.documentElement.style.setProperty("--sidebar-width", `${w}px`);
    sidebarWidthPx.value = w;
  },
  { immediate: true },
);
watch(
  [shouldShowNavbar, () => bp.smaller.lg],
  ([showNavbar, isBelowDesktop]) => {
    document.documentElement.style.setProperty(
      "--mobile-navbar-height",
      showNavbar && isBelowDesktop ? "3.5rem" : "0px",
    );
  },
  { immediate: true },
);

// Width reserved by the docked SidePanel so the fixed bars stop at its edge.
// A drag writes the variable itself, per frame; this covers every other change.
watch(
  [() => isSidepanelDocked.value, () => sidepanelDock.dockWidth.value],
  ([docked, width]) => {
    writeSidepanelWidth(docked, width);
  },
  { immediate: true },
);

const queryClient = useQueryClient();
const showRefreshPopup = useNewVersionCheck();

function onRefreshPage(): void {
  location.reload();
}

watch(
  () => authStore.isAuthed,
  (isAuthed) => {
    if (!isAuthed) {
      router.replace(loginRedirect(authStore, router.currentRoute.value));
      queryClient.clear();
      resetSidepanelState();
      resetAiChatSession();
    }
  },
);

onSelectedOrganizationChange(() => {
  resetSidepanelState();
  resetAiChatSession();
});

watchRouteForAccessTier();
watchRouteForUserRole();
watchRouteForOrganization();
watchRouteForPermissionRequirement();
watchRouteForFeatureFlag();
watchRouteForResetFilters();

useLogoutInterceptor();
useOrganizationChangedInterceptor();
useRequiresAuthNavigationGuard();
useHomeRouteRedirectGuard();
useSetDefaultTracking();
useTrackLogin();

useHead({
  titleTemplate: (title?: string) => (title ? `${title} | Fyr` : "Fyr"),
  title: useRouteTitle(),
});

useUpdateTemporalFilter();
</script>
